import { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useT } from "../contexts/TranslationContext";

export const TIME_REPORT_MARKER_OPTIONS = [
  { id: "deviation", labelKey: "reportFlags.deviation" },
  { id: "ata", labelKey: "reportFlags.ata" },
  { id: "internalNote", labelKey: "report.internalNote" },
];

/** Selected marker labels in checkbox order. */
export function timeReportMarkerLabels(markers, t) {
  const selected = new Set(markers);
  return TIME_REPORT_MARKER_OPTIONS.filter((option) => selected.has(option.id)).map((option) =>
    t(option.labelKey)
  );
}

export default function TimeReportMarkerFilter({ markers = [], onChange }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const labels = timeReportMarkerLabels(markers, t);
  const active = labels.length > 0;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggleMarker = (id) => {
    const selected = new Set(markers);
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
    onChange(
      TIME_REPORT_MARKER_OPTIONS.map((option) => option.id).filter((optionId) => selected.has(optionId))
    );
  };

  return (
    <div className="relative min-w-0" ref={containerRef}>
      <button
        type="button"
        className={`flex max-w-[11rem] items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset transition-colors sm:max-w-[18rem] ${
          active
            ? "bg-blue-50 text-blue-900 ring-blue-300 hover:bg-blue-100"
            : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50"
        }`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="truncate">
          {active ? labels.join(", ") : t("projectDetails.timeReportFilter")}
        </span>
        <FaChevronDown
          className={`h-3 w-3 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-gray-200 bg-white py-2 shadow-lg">
          <p className="px-3 pb-1 text-xs font-medium text-gray-500">
            {t("projectDetails.timeReportFilterHint")}
          </p>
          <ul>
            {TIME_REPORT_MARKER_OPTIONS.map((option) => (
              <li key={option.id}>
                <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 accent-blue-600"
                    checked={markers.includes(option.id)}
                    onChange={() => toggleMarker(option.id)}
                  />
                  {t(option.labelKey)}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-1 border-t border-gray-100 px-3 pt-2">
            <button
              type="button"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => onChange([])}
              disabled={!active}
            >
              {t("projectDetails.timeReportFilterClear")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
